/**
 * Магазин наборов за рубли (СБП). Цены — по статистике платежей; список и состав — src/core/packs.ts.
 */
import type { GameState } from '../../core/types';
import { PACKS, firstPurchaseBonus, newbieTimeLeft, packAvailable, type PackDef, type PackGrants } from '../../core/packs';
import { animalDef, buildingDef, clothingDef, petDef } from '../../core/registry';
import { cancelPending, demoPay, getPending, payConfig, startPayment, type PayConfig } from '../../pay/client';
import { fmtDuration, getLang, t, tl } from '../../i18n';
import { h, clearChildren } from '../dom';
import { openPanel } from '../modal';
import { toast } from '../toast';

const LOCALE: Record<string, string> = { ru: 'ru-RU', en: 'en-US', zh: 'zh-CN' };
const TAG_KEY = { hit: 'packs.hit', best: 'packs.best', popular: 'packs.popular' } as const;

export interface PacksCtx { state: GameState; now: () => number; check: () => Promise<void> }

/** Создать платёж за набор (показывает ошибку игроку). Возвращает true, если платёж создан. */
export async function beginPurchase(pk: PackDef): Promise<boolean> {
  try { await startPayment(pk.id, pk.rub); return true; }
  catch (e) {
    const msg = (e as Error).message;
    toast(msg === 'offer unavailable' ? t('packs.offerGone') : t('packs.error', { msg }), 'bad');
    return false;
  }
}

/** Строки «что внутри» для карточки набора. */
export function packLines(g: PackGrants): string[] {
  const n = (v: number): string => v.toLocaleString(LOCALE[getLang()] ?? 'en-US');
  const names = (ids: string[], f: (id: string) => string): string => ids.map(f).join(', ');
  const out: string[] = [];
  if (g.coins) out.push(`🪙 ${t('packs.coins', { n: n(g.coins) })}`);
  if (g.food) out.push(`🥕 ${t('packs.food', { n: n(g.food) })}`);
  if (g.xp) out.push(`⭐ ${t('packs.xp', { n: n(g.xp) })}`);
  if (g.fullEnergy) out.push(`⚡ ${t('packs.energy')}`);
  if (g.rodMax) out.push(`🎣 ${t('packs.rod')}`);
  if (g.islandTiers) out.push(`🏝️ ${t('packs.island', { n: g.islandTiers })}`);
  if (g.buildings?.length) out.push(`🏠 ${t('packs.buildings', { list: names(g.buildings, (id) => tl(buildingDef(id).name)) })}`);
  if (g.animals?.length) out.push(`🐄 ${t('packs.animals', { list: names(g.animals, (id) => tl(animalDef(id).name)) })}`);
  if (g.pets?.length) out.push(`🐾 ${t('packs.pets', { list: names(g.pets, (id) => tl(petDef(id).name)) })}`);
  if (g.clothing?.length) out.push(`👗 ${t('packs.clothing', { list: names(g.clothing, (id) => tl(clothingDef(id).name)) })}`);
  return out;
}

export function openPacks(ctx: PacksCtx): void {
  let cfg: PayConfig | null = null;
  let busy = false;
  let box: HTMLElement | null = null;

  const render = (): void => {
    if (!box) return;
    clearChildren(box);
    const pending = getPending();
    box.append(h('div', { class: 'packs-note' }, t('packs.sbp')));

    if (pending) {
      const pk = PACKS.find((p) => p.id === pending.packId);
      box.append(h('div', { class: 'packs-pending' },
        h('div', {}, `⏳ ${t('packs.pending')} ${pk ? `· ${tl(pk.name)} · ${pending.rub} ₽` : ''}`),
        h('div', { class: 'packs-pending-btns' },
          h('button', { class: 'btn btn-sm btn-primary', onclick: () => void check(true) }, t('packs.check')),
          pending.demo ? h('button', { class: 'btn btn-sm', onclick: () => void simulate() }, t('packs.demo')) : null,
          h('button', { class: 'btn btn-sm', onclick: () => { cancelPending(); render(); } }, t('packs.cancel')),
        ),
      ));
    }
    if (cfg && !cfg.enabled) box.append(h('div', { class: 'packs-warn' }, t('packs.unavailable')));

    const grid = h('div', { class: 'packs-grid' });
    const shown = PACKS.filter((p) => p.kind !== 'pass' && packAvailable(ctx.state, p, ctx.now()));
    shown.sort((a, b) => (a.kind === 'newbie' ? -1 : 0) - (b.kind === 'newbie' ? -1 : 0) || a.rub - b.rub);
    for (const pk of shown) grid.append(card(pk));
    box.append(grid);
  };

  const card = (pk: PackDef): HTMLElement => h('div', { class: `pack-card ${pk.tag ?? ''} ${pk.kind === 'newbie' ? 'newbie' : ''}` },
    pk.tag ? h('div', { class: 'pack-tag' }, t(TAG_KEY[pk.tag])) : null,
    h('div', { class: 'pack-title' }, `${pk.icon} ${tl(pk.name)}`),
    pk.kind === 'newbie' ? h('div', { class: 'pack-badge' }, `⏳ ${t('packs.newbie', { t: fmtDuration(newbieTimeLeft(ctx.state, ctx.now())) })}`) : null,
    firstPurchaseBonus(ctx.state, pk) && pk.grants.coins ? h('div', { class: 'pack-badge x2' }, `✨ ${t('packs.firstx2')}`) : null,
    h('ul', { class: 'pack-lines' }, ...packLines(pk.grants).map((l) => h('li', {}, l))),
    h('button', {
      class: 'btn btn-primary pack-buy',
      ...(busy || (cfg && !cfg.enabled) ? { disabled: 'true' } : {}),
      onclick: () => void buy(pk),
    }, t('packs.buy', { rub: pk.rub.toLocaleString(LOCALE[getLang()] ?? 'en-US') })),
  );

  async function buy(pk: PackDef): Promise<void> {
    if (busy) return;
    busy = true; render();
    await beginPurchase(pk);
    busy = false; render();
    void ctx.check();
  }
  async function simulate(): Promise<void> {
    try { await demoPay(); await ctx.check(); } catch (e) { toast(t('packs.error', { msg: (e as Error).message }), 'bad'); }
    render();
  }
  async function check(manual: boolean): Promise<void> {
    await ctx.check();
    if (manual && getPending()) toast(t('packs.notYet'), 'info');
    render();
  }

  const onUpdate = (): void => render();
  window.addEventListener('pay:update', onUpdate);

  openPanel({
    title: `💎 ${t('panel.packs')}`,
    className: 'panel-wide',
    onClose: () => window.removeEventListener('pay:update', onUpdate),
    body: (container) => {
      box = container;
      render();
      void payConfig().then((c) => { cfg = c; render(); });
    },
  });
}
